import { useEffect } from "react";
import { useForm, Controller, FieldError } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import FormText from "@/components/kit/FormGenerator/FormText";
import { ManualSubscriptionSimpleFormType } from "./form-type";
import { manualSubscriptionSimpleValidationSchema } from "./validation";

const defaultValues: ManualSubscriptionSimpleFormType = {
    userId: undefined as any,
    activeTier: "noun",
    hasSubscription: false,
};

// Active Tier enum values from FixUserStateBody
const ACTIVE_TIER_OPTIONS = [
    { value: "trial", label: "Trial" },
    { value: "normal", label: "Normal" },
    { value: "freeCountries", label: "Free Countries" },
];

interface ManualSubscriptionSimpleFormProps {
    loading: boolean;
    onSubmit: (data: ManualSubscriptionSimpleFormType, resetForm: () => void) => void;
}

const ManualSubscriptionSimpleForm: React.FC<ManualSubscriptionSimpleFormProps> = ({ loading, onSubmit }) => {
    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
        trigger,
    } = useForm<ManualSubscriptionSimpleFormType>({
        defaultValues,
        mode: "all",
        resolver: yupResolver(manualSubscriptionSimpleValidationSchema),
    });

    useEffect(() => {
        if (isValid) void trigger();
    }, [trigger, isValid]);

    return (
        <form
            style={{ maxWidth: "450px", margin: "0 auto", padding: "0 0 2em 0" }}
            onSubmit={handleSubmit((values) => onSubmit(values, () => reset(defaultValues)))}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Controller
                        name="userId"
                        control={control}
                        render={({ field }) => (
                            <FormText
                                field={{
                                    ...field,
                                    value: field.value || "",
                                    onChange: (e) => field.onChange(e.target.value ? Number(e.target.value) : undefined),
                                }}
                                label="User ID"
                                placeholder="Enter user ID"
                                errorMessage={errors.userId as FieldError}
                                props={{ type: "text" }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="activeTier"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth error={!!errors.activeTier}>
                                <InputLabel>Active Tier</InputLabel>
                                <Select value={field.value || "noun"} onChange={field.onChange} label="Active Tier">
                                    <MenuItem value="noun">
                                        <em>None</em>
                                    </MenuItem>
                                    {ACTIVE_TIER_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.activeTier && (
                                    <div style={{ color: "red", fontSize: "0.75rem", marginTop: "4px" }}>
                                        {errors.activeTier.message}
                                    </div>
                                )}
                            </FormControl>
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="hasSubscription"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={<Switch checked={field.value || false} onChange={field.onChange} color="primary" />}
                                label="Has Subscription"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoadingButton
                        className="loading-btn"
                        disabled={!isValid || loading}
                        type="submit"
                        variant="contained"
                        fullWidth
                        loading={loading}
                    >
                        Submit
                    </LoadingButton>
                </Grid>
            </Grid>
        </form>
    );
};

export default ManualSubscriptionSimpleForm;
